import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingCTA from '../components/FloatingCTA';
import Button from '../components/Button';
import PartnerCategory from '../components/PartnerCategory';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePartnersByCategory } from '@/hooks/usePartners';

const NewPartner = () => {
  const { data: categorizedPartners, isLoading, error } = usePartnersByCategory();

  const categoryOrder = [
    'Holzbau',
    'Rohbau', 
    'Trockenbau',
    'Trocknung und Sanierung',
    'Elektriker – Planung und Ausführung',
    'Abdichtung',
    'Ingenieure und Architekten',
    'Baustoffe',
    'Finanz- und Immobilien Experten'
  ];

  // Filter Holzbau to only Steco Centar
  const holzbauPartners = categorizedPartners?.['Holzbau']?.filter(
    p => p.name === 'Steco Centar'
  ) || [];

  // Get ordered categories with partners
  const getPartnersForCategory = (category: string) => {
    if (category === 'Holzbau') return holzbauPartners;
    return categorizedPartners?.[category] || [];
  };

  const orderedCategories = categoryOrder.filter(category => 
    getPartnersForCategory(category).length > 0
  );

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-primary mb-4">Fehler beim Laden der Partner</h2>
            <p className="text-muted-foreground">Bitte versuchen Sie es später erneut.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="section-padding bg-primary text-white pt-32 relative overflow-hidden">
        <div className="container-custom text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-8">Unsere Partner</h1>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed opacity-90">
            Vertrauen Sie auf bewährte Partnerschaften. Gemeinsam mit zuverlässigen Partnern realisieren wir Ihre Bauprojekte mit höchster Qualität und langjähriger Erfahrung.
          </p>
        </div>
      </section>

      {/* Partner Categories */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          {orderedCategories.map((category) => (
            <PartnerCategory 
              key={category} 
              category={category} 
              partners={getPartnersForCategory(category)} 
            />
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-4xl font-bold text-primary">
              Bereit für Ihr Partnerprojekt?
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Nutzen Sie die Expertise unserer bewährten Partner für Ihr nächstes Bauvorhaben. 
              Lassen Sie uns gemeinsam innovative Lösungen entwickeln.
            </p>
            <div className="pt-8">
              <Link to="/kontakt">
                <Button variant="primary" size="lg" icon={<ArrowRight className="w-5 h-5" />}>
                  Partnerlösungen entdecken
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
};

export default NewPartner;
